import { useEffect, useState } from "react";
import TablaContratosVista from "../vista/tablaContratosVista";
import type { IContrato } from "../../../modelos/Contratos/entidades/IContrato";
import { getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { columnasTablaContrato } from "../vista/constantesTablaContratos";
import { estadoInicialContrato } from "../interfaces";
import React from "react";
import { EnumTipoNotificacion, type ICatalogo } from "../../../globales/interfaces";
import { Box, Tabs } from "@mantine/core";
import FormularioContratoVista from "../vista/formularioContratoVista";
import { ContratoModelo } from "../../../modelos/Contratos";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import { useProyecto } from "../../proyectos/contextoProyecto";
import { useCargando } from "../../../globales/contextos/cargandoContext";

interface IContratosCVProps {
    pantallaCompleta?: boolean;
}

const ContratosCV: React.FunctionComponent<IContratosCVProps> = ({  }) => {
    const TAMANIO_PAGINA = 8;
    const modeloContrato = new ContratoModelo();
    const modeloProyecto = new ProyectoModelo();
    const { mostrarNotificacion } = useNotificaciones();
    const { proyecto, setProyecto } = useProyecto();
    const { manejarCargando } = useCargando();

    const [listaContratos, setListaContratos] = useState<IContrato[]>([]);
    const [indicePagina, setIndicePagina] = useState(0);
    const [totalContratos, setTotalContratos] = useState(0);
    const [habilitarFiltros, setHabilitarFiltros] = useState(false);
    const [filtro, setFiltro] = useState<IContrato>(estadoInicialContrato);
    const [enCambioContrato, setEnCambioContrato] = useState(false);

    const [tiposContrato, setTiposContrato] = useState<ICatalogo[]>([])
    const [contrato, setContrato] = useState<IContrato>({ ...estadoInicialContrato, ProyectoId: proyecto.Id });
    const [errores, setErrores] = useState<Partial<Record<keyof IContrato, string | null>>>({});

    useEffect(() => {
        consultarTiposContrato();
    }, []);

    const consultarContratoActivo = async (contratoId: number) => {
        try {
            manejarCargando(true);
            if (contratoId !== 0 && contratoId !== null && contratoId !== undefined) {
                const contratoActivo = await modeloContrato.obtenerContratoFiltros({
                    NumeroContrato: null,
                    Activo: true,
                    FechaFin: null,
                    FechaInicio: null,
                    Id: contratoId
                });
                setContrato(contratoActivo.Data);
            }
        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error de conexión',
                'Ocurrió un error al cargar el contrato activo. Por favor intenta más tarde.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    }

    const consultarContratos = async (page: number, size: number) => {
        try {
            manejarCargando(true);
            const contratos = await modeloContrato.obtenerListaHistorialClientesCompuesto(
                proyecto.Id,
                { Page: page + 1, Size: size }
            );
            setListaContratos(contratos.Data)
            setTotalContratos(contratos.Pagination.Total ?? contratos.Data.length);
        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error de conexión',
                'No se pudo conectar con el servidor. Por favor intenta más tarde.',
                5000
            );
        }
        finally {
            manejarCargando(false);
        }
    }

    const consultarTiposContrato = async () => {
        const tipos = await modeloContrato.consultarTiposContrato();
        setTiposContrato(tipos.Data)
        return tipos
    }

    const tabla = useReactTable({
        data: listaContratos,
        columns: columnasTablaContrato(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: true,
        state: { pagination: { pageIndex: indicePagina, pageSize: TAMANIO_PAGINA } },
        onPaginationChange: updater => {
            if (typeof updater === "function") {
                const newState = updater({ pageIndex: indicePagina, pageSize: TAMANIO_PAGINA });
                setIndicePagina(newState.pageIndex);
                consultarContratos(newState.pageIndex, newState.pageSize);
            } else {
                setIndicePagina(updater.pageIndex ?? 0);
                consultarContratos(updater.pageIndex ?? 0, TAMANIO_PAGINA);
            }
        },
        pageCount: Math.ceil(totalContratos / TAMANIO_PAGINA),
    });

    const manejarCambioValorContrato = (campo: keyof IContrato, valor: IContrato[keyof IContrato]) => {
        setContrato((prevContrato) => ({
            ...prevContrato,
            [campo]: valor
        }));
    }

    const manejarHabilitarFiltros = (habilitar: boolean) => {
        setHabilitarFiltros(habilitar)
    }

    const manejarCambioFiltroContrato = (campo: keyof IContrato, valor: IContrato[keyof IContrato]) => {
        setFiltro((prevFiltro) => ({
            ...prevFiltro,
            [campo]: valor
        }));
    }

    const agregarError = async (campo: keyof IContrato, error: string) => {
        setErrores((prevErrores) => ({
            ...prevErrores,
            [campo]: error
        }));
    }

    const validarCamposContrato = () => {
        let esValido = true;

        if (!contrato.Numero.trim()) {
            agregarError("Numero", "Este campo es requerido");
            esValido = false;
        }

        if (contrato.TipoContratoId === 0) {
            agregarError("TipoContratoId", "Selecciona un tipo de contrato");
            esValido = false;
        }

        if (!contrato.FechaInicio.trim()) {
            agregarError("FechaInicio", "Este campo es requerido");
            esValido = false;
        }

        if (contrato.VigenciaMeses === 0) {
            agregarError("VigenciaMeses", "Este campo es requerido");
            esValido = false;
        }

        // if (contrato.ValorMensual === 0) {
        //     agregarError("ValorMensual", "Este campo es requerido");
        //     esValido = false;
        // }

        if (!esValido) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Campos requeridos (*)",
                "Por favor completa todos los campos antes de continuar.",
                3000
            );
        }

        return esValido;
    };

    const crearContrato = async () => {
        if (!validarCamposContrato()) return;
        manejarCargando(true);
        try {
            let contratoCreado;
            try {
                const contratoRespuesta = await modeloContrato.crearContrato({ ...contrato, ProyectoId: proyecto.Id });

                if (contratoRespuesta.NotificationType === EnumTipoNotificacion.Success && contratoRespuesta.Data) {
                    contratoCreado = contratoRespuesta.Data;
                    setContrato(contratoCreado);
                }

                if (contratoRespuesta.Messages.length > 0) {
                    contratoRespuesta.Messages.forEach((mensaje) =>
                        mostrarNotificacion(
                            contratoRespuesta.NotificationType,
                            "",
                            mensaje,
                            4000
                        )
                    );
                }

                if (!contratoCreado) {
                    throw new Error("No se pudo crear el contrato.");
                }
            } catch {
                mostrarNotificacion(
                    EnumTipoNotificacion.Error,
                    "Error al crear contrato",
                    "No se pudo crear el contrato. Por favor verifica los datos e intenta nuevamente.",
                    10000
                );
                return;
            }


            try {
                const respuestaEdicionProyecto = await modeloProyecto.editarProyecto({
                    ...proyecto,
                    Ciudad: undefined,
                    Cliente: undefined,
                    Pais: undefined,
                    Regional: undefined,
                    Zona: undefined,
                    Contrato: undefined,
                    ContratoId: contratoCreado.Id,
                });

                if (
                    respuestaEdicionProyecto.NotificationType === EnumTipoNotificacion.Success &&
                    respuestaEdicionProyecto.Data &&
                    respuestaEdicionProyecto.Data.ContratoId
                ) {
                    setProyecto({
                        ...proyecto,
                        ContratoId: respuestaEdicionProyecto.Data.ContratoId,
                    });
                    consultarContratoActivo(respuestaEdicionProyecto.Data.ContratoId);
                }
            } catch {
                mostrarNotificacion(
                    EnumTipoNotificacion.Warning,
                    "Contrato creado, pero no asociado",
                    "El contrato fue creado exitosamente, pero no se pudo asociar al proyecto. Por favor consulta el contrato creado e intenta asociarlo nuevamente.",
                    10000
                );
            }
        } catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "No se pudo completar la acción",
                "Ocurrió un problema al conectar con el servidor. Por favor intenta nuevamente en unos minutos.",
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };


    const editarContrato = async () => {
        if (!validarCamposContrato()) return;
        manejarCargando(true);
        try {
            let contratoEditado;
            try {
                const contratoRespuesta = await modeloContrato.editarContrato({ ...contrato, ProyectoId: proyecto.Id });

                if (contratoRespuesta.NotificationType === EnumTipoNotificacion.Success && contratoRespuesta.Data) {
                    contratoEditado = contratoRespuesta.Data;
                    setContrato(contratoEditado);
                }

                if (contratoRespuesta.Messages.length > 0) {
                    contratoRespuesta.Messages.forEach((mensaje) =>
                        mostrarNotificacion(
                            contratoRespuesta.NotificationType,
                            "",
                            mensaje,
                            4000
                        )
                    );
                }

                if (!contratoEditado) {
                    throw new Error("No se pudo editar el contrato.");
                }
            } catch {
                mostrarNotificacion(
                    EnumTipoNotificacion.Error,
                    "No se pudo editar el contrato",
                    "No fue posible actualizar la información del contrato. Por favor revisa los datos e intenta nuevamente.",
                    10000
                );
                return;
            }

            if (proyecto.ContratoId !== contratoEditado.Id) {
                try {
                    const respuestaEdicionProyecto = await modeloProyecto.editarProyecto({
                        ...proyecto,
                        Ciudad: undefined,
                        Cliente: undefined,
                        Pais: undefined,
                        Regional: undefined,
                        Zona: undefined,
                        ContratoId: contratoEditado.Id,
                    });

                    if (
                        respuestaEdicionProyecto.NotificationType === EnumTipoNotificacion.Success &&
                        respuestaEdicionProyecto.Data &&
                        respuestaEdicionProyecto.Data.ContratoId
                    ) {
                        setProyecto({
                            ...proyecto,
                            ContratoId: respuestaEdicionProyecto.Data.ContratoId,
                        });
                        consultarContratoActivo(respuestaEdicionProyecto.Data.ContratoId);
                    }
                } catch {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Warning,
                        "Contrato actualizado, pero no asociado",
                        "La edición del contrato fue exitosa, pero no se pudo asociar al proyecto. Por favor consulta el contrato actualizado e intenta asociarlo nuevamente.",
                        10000
                    );
                }
            }
        } catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "No se pudo completar la acción",
                "Ocurrió un inconveniente al conectar con el servidor. Por favor intenta de nuevo en unos minutos.",
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };


    const crearEditarContrato = () => {
        if (contrato.Id === 0) {
            crearContrato();
        }
        else {
            editarContrato();
        }
    }

    const renovarContrato = (estado: boolean) => {
        setEnCambioContrato(estado);
        if (estado) {
            setContrato(estadoInicialContrato);
            setErrores({});
        } else {
            if (proyecto.ContratoId)
                consultarContratoActivo(proyecto.ContratoId);
        }
    }

    return (
        <Box p='md'>
            <Tabs keepMounted={false} variant="outline" defaultValue="ContratoActivo" >
                <Tabs.List >
                    <Tabs.Tab value="ContratoActivo" >
                        Contrato Activo
                    </Tabs.Tab>
                    <Tabs.Tab value="Historico" >
                        Historico
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="ContratoActivo">
                    <Box pt="md">
                        <FormularioContratoVista
                            agregarError={agregarError}
                            contratoSeleccionado={contrato}
                            manejarCambioValorContrato={manejarCambioValorContrato}
                            errores={errores}
                            tiposContrato={tiposContrato}
                            guardarEditarContrato={crearEditarContrato}
                            cambiarContrato={renovarContrato}
                            enCambioContrato={enCambioContrato}
                            consultarContratoActivo={consultarContratoActivo}
                        />
                    </Box>
                </Tabs.Panel>
                <Tabs.Panel value="Historico" >
                   <Box pt="xs">
                        <TablaContratosVista
                            tabla={tabla}
                            consultarContratos={consultarContratos}
                            TAMANIO_PAGINA={TAMANIO_PAGINA}
                            filtro={filtro}
                            manejarHabilitarFiltros={manejarHabilitarFiltros}
                            habilitarFiltros={habilitarFiltros}
                            manejarCambioFiltroContrato={manejarCambioFiltroContrato}
                            indicePagina={indicePagina}
                        />
                    </Box>
                </Tabs.Panel>
            </Tabs>
        </Box>
    )
}

export default React.memo(ContratosCV);